import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { streamChatbotResponse } from '../../../services/chatbotService';

const TravelChatbot = ({ userPreferences = {} }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I\'m your Philippine travel assistant. Ask me anything about destinations, activities, local culture, or get personalized recommendations!',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!inputMessage?.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage?.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setError('');
    setLoading(true);
    setIsStreaming(true);

    // Create placeholder for assistant response
    const assistantMessageId = Date.now();
    setMessages(prev => [...prev, {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    }]);

    try {
      const conversationHistory = messages?.filter(msg => msg?.role !== 'assistant' || !msg?.isStreaming)?.map(msg => ({
          role: msg?.role,
          content: msg?.content
        }));
      conversationHistory?.push({ role: 'user', content: userMessage?.content });

      let fullResponse = '';

      await streamChatbotResponse(
        conversationHistory,
        (chunk) => {
          fullResponse += chunk;
          setMessages(prev => 
            prev?.map(msg => 
              msg?.id === assistantMessageId
                ? { ...msg, content: fullResponse }
                : msg
            )
          );
        },
        userPreferences
      );

      // Mark streaming as complete
      setMessages(prev => 
        prev?.map(msg => 
          msg?.id === assistantMessageId
            ? { ...msg, isStreaming: false }
            : msg
        )
      );
    } catch (err) {
      setError(err?.message || 'Failed to get response. Please try again.');
      // Remove the placeholder message on error
      setMessages(prev => prev?.filter(msg => msg?.id !== assistantMessageId));
    } finally {
      setLoading(false);
      setIsStreaming(false);
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        role: 'assistant',
        content: 'Hi! I\'m your Philippine travel assistant. Ask me anything about destinations, activities, local culture, or get personalized recommendations!',
        timestamp: new Date()
      }
    ]);
    setError('');
  };

  const suggestedQuestions = [
    'What are the best beaches in the Philippines?',
    'Recommend activities for adventure travelers',
    'What\'s the best time to visit Palawan?',
    'Tell me about Filipino food culture'
  ];

  const handleSuggestedQuestion = (question) => {
    setInputMessage(question);
  };

  return (
    <div className="bg-card rounded-2xl shadow-xl border border-border overflow-hidden flex flex-col h-[600px]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2E8B8B] to-[#F4A261] p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 rounded-full p-2">
            <Icon name="MessageCircle" size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-lg">Travel Assistant</h3>
            <p className="text-white/80 text-xs">Powered by AI</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearChat}
          iconName="Trash2"
          className="text-white hover:bg-white/20"
        >
          Clear
        </Button>
      </div>

      {/* Messages Container */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-background to-muted/20"
      >
        {messages?.map((message, index) => (
          <div
            key={index}
            className={`flex gap-3 ${message?.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
          >
            {/* Avatar */}
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
              message?.role === 'user' ?'bg-[#F4A261]' :'bg-[#2E8B8B]'
            }`}>
              <Icon 
                name={message?.role === 'user' ? 'User' : 'Bot'} 
                size={16} 
                className="text-white" 
              />
            </div>

            {/* Message Bubble */}
            <div className={`flex-1 max-w-[80%] ${
              message?.role === 'user' ? 'text-right' : 'text-left'
            }`}>
              <div className={`inline-block rounded-2xl px-4 py-2 ${
                message?.role === 'user' ?'bg-[#F4A261] text-white' :'bg-card border border-border text-foreground'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{message?.content}</p>
                {message?.isStreaming && (
                  <span className="inline-block w-2 h-4 bg-current animate-pulse ml-1" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 px-2">
                {message?.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Questions (only show when chat is empty) */}
      {messages?.length === 1 && (
        <div className="px-4 py-2 border-t border-border bg-muted/30">
          <p className="text-xs text-muted-foreground mb-2">Suggested questions:</p>
          <div className="flex flex-wrap gap-2">
            {suggestedQuestions?.map((question, index) => (
              <button
                key={index}
                onClick={() => handleSuggestedQuestion(question)}
                className="text-xs bg-card border border-border rounded-full px-3 py-1 hover:bg-accent transition-colors"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="px-4 py-2 bg-destructive/10 border-t border-destructive/20">
          <div className="flex items-center gap-2 text-destructive text-sm">
            <Icon name="AlertCircle" size={16} />
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Input Area */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-border bg-card">
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Ask about destinations, activities, or get recommendations..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e?.target?.value)}
              disabled={loading}
              className="w-full"
            />
          </div>
          <Button
            type="submit"
            disabled={!inputMessage?.trim() || loading}
            loading={loading}
            iconName="Send"
            className="bg-gradient-to-r from-[#2E8B8B] to-[#F4A261]"
          >
            Send
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TravelChatbot;
